/**
 * Decodes a JWT token and returns the payload (claims)
 * @param {string} token - JWT token string
 * @returns {object|null} - Decoded payload or null if invalid
 */
export const decodeJWT = (token) => {
    if (!token || typeof token !== 'string') {
        return null;
    }

    try {
        // JWT has three parts: header.payload.signature
        const parts = token.split('.');
        if (parts.length !== 3) {
            return null;
        }

        // Decode the payload (second part)
        const payload = parts[1];
        
        // Replace URL-safe characters and add padding if needed
        let base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
        const padding = base64.length % 4;
        if (padding) {
            base64 += '='.repeat(4 - padding);
        }

        // Decode from Base64
        const decoded = atob(base64);
        
        // Parse JSON
        return JSON.parse(decoded);
    } catch (error) {
        console.error('Error decoding JWT:', error);
        return null;
    }
};

/**
 * Extracts custom claims from JWT token
 * Custom claims are all claims except standard Firebase claims
 * @param {string} token - JWT token string
 * @returns {object} - Object with custom claims
 */
export const getCustomClaims = (token) => {
    const payload = decodeJWT(token);
    if (!payload) {
        return {};
    }

    // Standard Firebase claims to exclude
    const standardClaims = [
        'iss', 'aud', 'auth_time', 'sub', 'iat', 'exp', 'firebase', 'email', 
        'email_verified', 'name', 'picture', 'user_id'
    ];

    const customClaims = {};
    
    Object.keys(payload).forEach(key => {
        // Include if not a standard claim or if it's clearly a custom claim
        if (!standardClaims.includes(key)) {
            customClaims[key] = payload[key];
        }
    });

    return customClaims;
};

/**
 * UserStatus enum matching backend
 */
export const UserStatus = {
    Pending: 0,
    Active: 1,
    Cancelled: 2,
    Rejected: 3,
    NotFound: 4
};

/**
 * UserStatus enum names
 */
export const UserStatusNames = {
    0: 'Pending',
    1: 'Active',
    2: 'Cancelled',
    3: 'Rejected',
    4: 'NotFound'
};

/**
 * Gets the UserStatus numeric value from a string name
 * @param {string} statusName - Status name (e.g., "Active")
 * @returns {number} - Numeric status value
 */
export const getUserStatusValue = (statusName) => {
    if (typeof statusName === 'number') {
        return statusName;
    }
    return UserStatus[statusName] ?? UserStatus.NotFound;
};

/**
 * Gets the UserStatus name from a numeric value or string
 * @param {number|string} value - Numeric status value or status name
 * @returns {string} - Status name
 */
export const getUserStatusName = (value) => {
    // If already a string, return it
    if (typeof value === 'string') {
        return value;
    }
    // If numeric, convert to name
    return UserStatusNames[value] ?? String(value);
};

/**
 * Formats claim value for display
 * @param {*} value - Claim value
 * @param {string} claimName - Optional claim name for special formatting
 * @returns {string} - Formatted string
 */
export const formatClaimValue = (value, claimName = null) => {
    if (value === null || value === undefined) {
        return 'null';
    }
    
    // Special handling for status claim - now it comes as string from backend
    if (claimName === 'status') {
        return getUserStatusName(value);
    }
    
    if (typeof value === 'object') {
        return JSON.stringify(value, null, 2);
    }
    
    if (typeof value === 'boolean') {
        return value ? 'true' : 'false';
    }
    
    return String(value);
};

